import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const AuditLogsContainer = styled.div`
  max-width: 1560px;
  margin: 0 auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid var(--gray-200);
    border-top: 4px solid var(--primary-600);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ActionBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => {
    if (props.action?.includes('CREATE') || props.action?.includes('ADD')) return '#d4edda';
    if (props.action?.includes('DELETE') || props.action?.includes('REMOVE')) return '#f8d7da';
    if (props.action?.includes('UPDATE') || props.action?.includes('EDIT')) return '#fff3cd';
    if (props.action?.includes('LOGIN') || props.action?.includes('AUTH')) return '#cce5ff';
    return '#e2e3e5';
  }};
  color: ${props => {
    if (props.action?.includes('CREATE') || props.action?.includes('ADD')) return '#155724';
    if (props.action?.includes('DELETE') || props.action?.includes('REMOVE')) return '#721c24';
    if (props.action?.includes('UPDATE') || props.action?.includes('EDIT')) return '#856404';
    if (props.action?.includes('LOGIN') || props.action?.includes('AUTH')) return '#004085';
    return '#383d41';
  }};
`;

function AuditLogs() {
  const { isDark } = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    pages: 1,
    total: 0,
    per_page: 20
  });
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_id: ''
  });

  const loadLogs = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '20'
      });

      if (filters.action) params.append('action', filters.action);
      if (filters.resource_type) params.append('resource_type', filters.resource_type);
      if (filters.user_id) params.append('user_id', filters.user_id);

      const response = await api.get(`/admin/audit-logs?${params}`);
      setLogs(response.data.audit_logs || []);
      setPagination(response.data.pagination || { page: 1, pages: 1, total: 0, per_page: 20 });
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getActionIcon = (action) => {
    if (action?.includes('CREATE') || action?.includes('ADD')) return '➕';
    if (action?.includes('DELETE') || action?.includes('REMOVE')) return '🗑️';
    if (action?.includes('UPDATE') || action?.includes('EDIT')) return '✏️';
    if (action?.includes('LOGIN')) return '🔐';
    if (action?.includes('LOGOUT')) return '🚪';
    return '📝';
  };

  return (
    <AuditLogsContainer>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <Link to="/" className="breadcrumb-link">Home</Link>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Audit Logs</span>
      </nav>

      {/* Header Card */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div>
            <h1 style={{
              fontSize: '1.5rem',
              color: 'var(--gray-900)',
              margin: '0 0 0.25rem 0',
              fontWeight: 'bold'
            }}>
              📋 Audit Logs
            </h1>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Track all system activity and user actions
            </p>
          </div>
          <button 
            onClick={() => loadLogs(pagination.page)}
            className="btn btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ width: '16px', height: '16px' }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--gray-500)',
              textTransform: 'uppercase'
            }}>
              Action Type
            </label>
            <select 
              value={filters.action} 
              onChange={(e) => handleFilterChange('action', e.target.value)}
              className="form-select"
              style={{ minWidth: '150px' }}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
            </select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <label style={{
              fontSize: '0.75rem',
              fontWeight: '600',
              color: 'var(--gray-500)',
              textTransform: 'uppercase'
            }}>
              Resource Type
            </label>
            <select 
              value={filters.resource_type} 
              onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              className="form-select"
              style={{ minWidth: '150px' }}
            >
              <option value="">All Resources</option>
              <option value="PROJECT">Project</option>
              <option value="CAMPAIGN">Campaign</option>
              <option value="LEAD">Lead</option>
              <option value="CONTENT">Content</option>
              <option value="USER">User</option>
              <option value="TENANT">Tenant</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="card" style={{ overflow: 'hidden', padding: 0 }}>
        {/* Table Header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '180px 140px 200px 1fr 120px',
          padding: '1rem 1.5rem',
          background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--gray-50)',
          fontWeight: '600',
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          color: 'var(--gray-500)',
          borderBottom: `1px solid var(--gray-200)`
        }}>
          <div>Timestamp</div>
          <div>Action</div>
          <div>Resource</div>
          <div>Details</div>
          <div>User</div>
        </div>

        {loading ? (
          <LoadingContainer>
            <div className="spinner"></div>
          </LoadingContainer>
        ) : logs.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem',
            color: 'var(--gray-500)'
          }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📋</div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: 'var(--gray-900)' }}>No audit logs found</h3>
            <p>Activity logs will appear here as users interact with the system.</p>
          </div>
        ) : (
          logs.map((log, index) => {
            const { date, time } = formatDate(log.created_at);
            return (
              <div 
                key={log.id} 
                style={{
                  display: 'grid',
                  gridTemplateColumns: '180px 140px 200px 1fr 120px',
                  padding: '1rem 1.5rem',
                  borderBottom: index < logs.length - 1 ? `1px solid var(--gray-200)` : 'none',
                  alignItems: 'center',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? 'var(--gray-700)' : 'var(--gray-50)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
                  <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{date}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{time}</div>
                </div>
                <div>
                  <ActionBadge action={log.action}>
                    {getActionIcon(log.action)} {log.action}
                  </ActionBadge>
                </div>
                <div style={{ fontSize: '0.875rem' }}>
                  <div style={{ fontWeight: '500', color: 'var(--gray-900)' }}>{log.resource_type}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>ID: {log.resource_id}</div>
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)', wordBreak: 'break-word' }}>
                  {log.details || '-'}
                </div>
                <div style={{ fontSize: '0.875rem', color: 'var(--gray-900)' }}>
                  {log.user_email || `User #${log.user_id}`}
                </div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!loading && logs.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1rem 1.5rem',
            background: isDark ? 'rgba(255,255,255,0.05)' : 'var(--gray-50)',
            borderTop: `1px solid var(--gray-200)`
          }}>
            <div style={{ fontSize: '0.875rem', color: 'var(--gray-500)' }}>
              Showing {((pagination.page - 1) * pagination.per_page) + 1} - {Math.min(pagination.page * pagination.per_page, pagination.total)} of {pagination.total} logs
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                onClick={() => loadLogs(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="btn btn-secondary"
                style={{
                  opacity: pagination.page <= 1 ? 0.5 : 1,
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Previous
              </button>
              <button 
                onClick={() => loadLogs(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="btn btn-secondary"
                style={{
                  opacity: pagination.page >= pagination.pages ? 0.5 : 1,
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </AuditLogsContainer>
  );
}

export default AuditLogs;
